import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import AssamMap from "../AssamMap";
import {
  EXPIRED_PRODUCTS,
  EXPIRED_FACILITIES,
  EXPIRED_CATEGORIES,
  EXPIRED_SKUS,
  EXPIRED_DISTRICT_DATA,
  EXPIRED_FACILITY_DATA,
  EXPIRED_CAT_DATA,
  EXPIRED_PROD_DATA,
} from "../../data/dashboardData";
import { hBarOptsNoMax } from "../../utils/chartHelpers";
import CardHead from "../../utils/CardHead";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels"; // ← import

// Register everything together here at the top level (outside the component)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels, // ← add here
);

export default function TabExpired() {
  const expiredProdData = useMemo(
    () => ({
      labels: EXPIRED_DISTRICT_DATA.map((p) => p.n),
      datasets: [
        {
          label: "Quantity",
          data: EXPIRED_DISTRICT_DATA.map((p) => p.qty),
          backgroundColor: "#dc2626",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#991b1b",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return v.toLocaleString();
            },
          },
        },
        {
          label: "Value (₹)",
          data: EXPIRED_DISTRICT_DATA.map((p) => p.value),
          backgroundColor: "#7c3aed",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x1",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#5b21b6",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return "₹" + (v / 1000).toFixed(0) + "K";
            },
          },
        },
      ],
    }),
    [],
  );

  const expiredFacData = useMemo(
    () => ({
      labels: EXPIRED_FACILITY_DATA.map((f) => f.name),
      datasets: [
        {
          label: "Quantity",
          data: EXPIRED_FACILITY_DATA.map((d) => d.qty),
          backgroundColor: "#dc2626",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#991b1b",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return v.toLocaleString();
            },
          },
        },
        {
          label: "Value (₹)",
          data: EXPIRED_FACILITY_DATA.map((d) => d.value),
          backgroundColor: "#7c3aed",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x1",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#5b21b6",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return "₹" + (v / 1000).toFixed(0) + "K";
            },
          },
        },
      ],
    }),
    [],
  );

  const expiredCatData = useMemo(
    () => ({
      labels: EXPIRED_CAT_DATA.map((c) => c.cat),
      datasets: [
        {
          label: "Quantity",
          data: EXPIRED_CAT_DATA.map((c) => c.qty),
          backgroundColor: "#dc2626",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#991b1b",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return v.toLocaleString();
            },
          },
        },
        {
          label: "Values (₹)",
          data: EXPIRED_CAT_DATA.map((c) => c.value),
          backgroundColor: "#7c3aed",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x1",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#5b21b6",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return "₹" + (v / 1000).toFixed(0) + "K";
            },
          },
        },
      ],
    }),
    [],
  );

  const expiredProData = useMemo(
    () => ({
      labels: EXPIRED_PROD_DATA.map((c) => c.name),
      datasets: [
        {
          label: "Quantity",
          data: EXPIRED_PROD_DATA.map((c) => c.qty),
          backgroundColor: "#dc2626",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#991b1b",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return v.toLocaleString();
            },
          },
        },
        {
          label: "Values (₹)",
          data: EXPIRED_PROD_DATA.map((c) => c.value),
          backgroundColor: "#7c3aed",
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.6,
          xAxisID: "x1",
          datalabels: {
            display: true,
            anchor: "end",
            align: "right",
            color: "#5b21b6",
            font: { size: 9, weight: "700" },
            formatter: function (v) {
              return "₹" + (v / 1000).toFixed(0) + "K";
            },
          },
        },
      ],
    }),
    [],
  );

  return (
    <div className={`viz-pane active`}>
      <div className="chart-grid-2" style={{ alignItems: "stretch" }}>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-map-marked-alt"
            title="📍 Expired Stock Heat Map"
            subtitle="Districts with expired medicines"
          />
          <div className="card-body" style={{ flex: 1, padding: 10 }}>
            <AssamMap mode="expired" />
          </div>
        </div>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-times-circle"
            title="🏛 District-wise Expired (Qty vs Value)"
            subtitle="Top 10 districts by expired quantity"
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            <Bar data={expiredProdData} options={hBarOptsNoMax(true)} />
          </div>
        </div>
      </div>
      <div
        className="chart-grid-3"
        style={{
          marginTop: 14,
          alignItems: "stretch",
        }}
      >
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-hospital"
            title="🏥 Facility-wise Expired (Qty vs Value)"
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            <Bar data={expiredFacData} options={hBarOptsNoMax(true)} />
          </div>
        </div>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-boxes-stacked"
            title="📦 Category Wise Expired (Qty vs Value)"
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            <Bar data={expiredCatData} options={hBarOptsNoMax(true)} />
          </div>
        </div>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-boxes-stacked"
            title="💊 Top 10 Product Expired (Qty vs Value)"
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            <Bar data={expiredProData} options={hBarOptsNoMax(true)} />
          </div>
        </div>
      </div>
    </div>
  );
}
